import React, { useEffect, useState } from 'react';
import { CheckSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemberPortal } from './MemberPortal';
import { getMyAttendances } from '../../lib/memberDb';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, getDay, parseISO, isSameDay } from 'date-fns';
import { ko } from 'date-fns/locale';

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];

export default function MyAttendance() {
  const { memberRecord } = useMemberPortal();
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(new Date());

  useEffect(() => {
    getMyAttendances(memberRecord.id).then(data => {
      setAttendances(data);
      setLoading(false);
    });
  }, [memberRecord.id]);

  const attendedDates = attendances.map(a => parseISO(a.date));
  const monthStart = startOfMonth(current);
  const monthEnd = endOfMonth(current);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart);

  const thisMonthCount = attendances.filter(a => {
    const d = parseISO(a.date);
    return isSameMonth(d, current);
  }).length;

  const totalCount = attendances.length;

  if (loading) return <div className="p-4 text-center text-gray-400 text-sm pt-12">불러오는 중...</div>;

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">출석 현황</h2>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{thisMonthCount}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">이번 달 출석</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{totalCount}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">전체 출석</p>
        </div>
      </div>

      {/* 달력 */}
      <div className="card p-4">
        {/* 월 이동 */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCurrent(d => { const n = new Date(d); n.setMonth(n.getMonth() - 1); return n; })} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
          <span className="font-semibold text-gray-900 dark:text-white text-sm">
            {format(current, 'yyyy년 M월', { locale: ko })}
          </span>
          <button onClick={() => setCurrent(d => { const n = new Date(d); n.setMonth(n.getMonth() + 1); return n; })} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <ChevronRight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map((d, i) => (
            <div key={d} className={`text-center text-xs font-medium py-1 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>{d}</div>
          ))}
        </div>

        {/* 날짜 */}
        <div className="grid grid-cols-7 gap-y-1">
          {Array.from({ length: startPad }).map((_, i) => <div key={`pad-${i}`} />)}
          {days.map(day => {
            const attended = attendedDates.some(d => isSameDay(d, day));
            const isToday = isSameDay(day, new Date());
            const dow = getDay(day);
            return (
              <div key={day.toISOString()} className="flex items-center justify-center py-1">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium transition-colors ${
                  attended
                    ? 'bg-primary-500 text-white'
                    : isToday
                    ? 'border-2 border-primary-400 text-primary-600 dark:text-primary-400'
                    : dow === 0
                    ? 'text-red-400 dark:text-red-400'
                    : dow === 6
                    ? 'text-blue-400 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {format(day, 'd')}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 최근 출석 */}
      {attendances.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">최근 출석</p>
          <div className="space-y-2">
            {attendances.slice(0, 5).map(a => (
              <div key={a.id} className="card px-4 py-3 flex items-center gap-3">
                <CheckSquare className="w-4 h-4 text-primary-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {format(parseISO(a.date), 'M월 d일 (EEE)', { locale: ko })}
                </span>
                <span className="text-xs text-gray-400 ml-auto">{a.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
